import React, { useState, useEffect } from 'react';
import { DataGridPro, GridSelectionModel, useGridApiRef } from '@mui/x-data-grid-pro';
import IconButton from '@mui/material/IconButton';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate } from 'react-router-dom';
import { getData, deleteData, postData } from '../utils/helper';
import { ToastContainer, toast, Bounce } from 'react-toastify';
import Chip from '@mui/material/Chip';
import { PropagateLoader } from "react-spinners";
import { set } from 'date-fns';

const CSSProperties = {
    display: "block",
    margin: "0 auto",
    borderColor: "red",
};

const Salary = () => {
    const iconColor = '#00BFFF';
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [selectionModel, setSelectionModel] = useState([]);
    const apiRef = useGridApiRef();
    let [loading, setLoading] = useState(false);

    const handleAddNew = () => {
        navigate('/salary-form');
    };

    const handleEdit = (id) => {
        const rowToEdit = rows.find(row => row.id === id);
        navigate('/salary-form', { state: { editData: rowToEdit } });
    };

    const handleDelete = async (data) => {
        // Implement delete functionality here
        const response = await deleteData('salaries/' + data.id);
        if (response.status === undefined) {
            setRows(rows.filter(item => item.id !== data.id));
            toast.success('Item deleted successfully', {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Bounce,
            });
        } else {
            toast.errpr('Error while deletion', {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Bounce,
            });
        }
    };

    const columns = [
        {
            field: '__check__',
            headerName: '',
            width: 50,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderHeader: () => null,
        },
        { field: 'id', headerName: 'ID', width: 50 },
        {
            field: 'employee.name',  // Still define the field path for sorting/filtering
            headerName: 'Employee Name',
            width: 150,
            renderCell: (params) => {
                // Access nested value safely
                const employeeName = params.row.employee?.name;
                return (
                    <span>
                        {employeeName || '-'}
                    </span>
                );
            }
        },
        { field: 'month', headerName: 'Month', width: 100 },
        { field: 'grade', headerName: 'Grade', width: 80 },
        { field: 'basic_salary', headerName: 'Basic Salary', width: 100 },
        { field: 'incentive', headerName: 'Incentive', width: 100 },
        { field: 'qr_bonus', headerName: 'QR Bonus', width: 100 },
        { field: 'deductions', headerName: 'Deductions', width: 100 },

        {
            field: 'net_salary',
            headerName: 'Net Salary',
            width: 120,
            renderCell: (params) => {
                const basic = parseInt(params.row.basic_salary) || 0;
                const incentive = parseInt(params.row.incentive) || 0;
                const qrBonus = parseInt(params.row.qr_bonus) || 0;
                const deductions = parseInt(params.row.deductions) || 0;
                const netSalary = (basic + incentive + qrBonus) - deductions;
                return `Rs.${netSalary.toLocaleString()}`; // Format as currency
            }
        },
        {
            field: 'status', headerName: 'Status', width: 100, renderCell: (params) => (
                <Chip
                    label={params.value}
                    color={params.value === 'Paid' ? 'success' : 'error'}
                    variant="outlined"
                    sx={{
                        borderRadius: '16px',
                        width: '80px'
                    }}
                />
            )
        },
        {
            field: 'updated_at',
            headerName: 'Date Time',
            width: 150,
            renderCell: (params) => {
                if (!params.value) return '';

                // Format the date manually
                const formatDate = (dateString) => {
                    const date = new Date(dateString);
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    const hours = String(date.getHours()).padStart(2, '0');
                    const minutes = String(date.getMinutes()).padStart(2, '0');
                    const seconds = String(date.getSeconds()).padStart(2, '0');

                    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                };

                return (
                    <div style={{ width: '100%' }}>
                        {formatDate(params.value)}
                    </div>
                );
            }
        },
        {
            field: 'edit',
            headerName: 'Edit',
            width: 80,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <Tooltip title="Edit">
                    <IconButton
                        onClick={() => handleEdit(params.row.id)}
                        size="small"
                        sx={{ color: iconColor }}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
        {
            field: 'delete',
            headerName: 'Delete',
            width: 80,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <Tooltip title="Edit">
                    <IconButton
                        onClick={() => handleDelete(params.row)}
                        size="small"
                        sx={{ color: iconColor }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    const handleSelectionChange = (newSelectionModel) => {
        // You can then use newSelectionModel to find the corresponding row data
        // For example, to get the selected row objects:
        setSelectionModel(newSelectionModel);
    };

    const handleSendEmail = async () => {
        setLoading(true);
        if (selectionModel.length === 0) {
            setLoading(false);
            toast.error('Please select at least one item to send email', {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Bounce,
            });
            return;
        } else {
            const array = [...selectionModel.ids]
            const response = await postData('send-email', { ids: array });
            setLoading(false);
            if (response.status === 401) {
                setLoading(false);
                navigate('/', { state: { message: "Session Expired" } });
                return;
            }
            if (response.responseCode === "00") {
                toast.success('Email sent successfully', {
                    position: "top-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: false,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                    transition: Bounce,
                    onClose: () => window.location.reload(),
                });
            } else {
                toast.error('Error while sending email', {
                    position: "top-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: false,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                    transition: Bounce,
                });
            }
        }
    }

    useEffect(() => {
        setLoading(true);
        const fetchData = async () => {
            try {
                const data = await getData('salaries');
                if (data.status === 401) {
                    navigate('/', { state: { message: "Session Expired" } });
                    setLoading(false);
                    return;
                }
                setLoading(false);
                setRows(data?.data);
            } catch (error) {
                setLoading(false);
                toast.error('Error Fetching Data', {
                    position: "top-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: false,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                    transition: Bounce,
                });
            }
        };
        fetchData();
    }, []);


    return (
        <>
            {loading && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    width: '100vw',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.8)', // Optional: semi-transparent white background
                    zIndex: 9999, // Ensure it's above other elements
                }}>
                    <PropagateLoader
                        color="#00BFFF"
                        loading={loading}
                        cssOverride={CSSProperties}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                    />
                </div>
            )}
            <Paper elevation={3} sx={{ p: 2, height: '800px' }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2
                }}>
                    <Typography variant="h6" component="h2">
                        Salary List
                    </Typography>
                    <ToastContainer />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Send Email">
                            <IconButton
                                aria-label="Send Email"
                                onClick={handleSendEmail}
                                size="large"
                                sx={{ color: iconColor }}
                            >
                                <SendIcon fontSize="medium" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Add New">
                            <IconButton
                                aria-label="Add New"
                                onClick={handleAddNew}
                                size="large"
                                sx={{ color: iconColor }}
                            >
                                <AddCircleIcon fontSize="medium" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                <div style={{ height: 500, width: '100%' }}>
                    <DataGridPro
                        rows={rows}
                        columns={columns}
                        pageSize={5}
                        rowsPerPageOptions={[5, 10, 20]}
                        checkboxSelection
                        onRowSelectionModelChange={handleSelectionChange}
                        selectionModel={selectionModel}
                        disableSelectionOnClick
                        disableColumnSelector
                        disableDensitySelector
                    />
                </div>
            </Paper>
        </>
    );
};

export default Salary;